import type { ColDef, ValueFormatterParams } from 'ag-grid-community';

import { TableHeaderWithTooltipIcon } from '~/components/common';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import type { TypeStockDate } from '~/types/stock.types';

/**
 * [재고관리 > 유통기한별 조회] 페이지 테이블
 */
const getStockDateColumnDefs = () => {
  const columnDefs: ColDef<TypeStockDate>[] = [
    {
      headerName: 'No',
      valueGetter: 'node.rowIndex + 1',
      width: 72,
    },
    {
      field: 'alterCode',
      headerName: '상품 바코드',
      width: 150,
    },
    {
      field: 'skuCode',
      headerName: 'SKU코드',
      width: 140,
    },
    {
      field: 'clusterName',
      headerName: '보관 센터',
      width: 80,
    },
    {
      field: 'stockType',
      headerName: '재고 유형',
      width: 80,
    },
    {
      field: 'temperatureType',
      headerName: '보관 유형',
      width: 92,
    },
    {
      field: 'manufactureDate',
      headerName: '제조일',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeStockDate, TypeStockDate['manufactureDate']>) =>
        formatDate(value, 'YYYY.MM.DD') ?? '-',
    },
    {
      field: 'expiredDate',
      headerName: '소비기한',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeStockDate, TypeStockDate['expiredDate']>) =>
        formatDate(value, 'YYYY.MM.DD') ?? '-',
    },
    {
      field: 'salesDeadLineDate',
      headerName: '판매 마감일',
      width: 100,
      valueFormatter: ({ value }: ValueFormatterParams<TypeStockDate, TypeStockDate['salesDeadLineDate']>) =>
        formatDate(value, 'YYYY.MM.DD') ?? '-',
    },
    {
      field: 'skuName',
      headerName: '상품명',
      minWidth: 240,
      flex: 1,
    },
    {
      field: 'totalQuantity',
      headerName: '전체 재고',
      cellStyle: { textAlign: 'right' },
      width: 80,
      valueFormatter: ({ value }: ValueFormatterParams<TypeStockDate, TypeStockDate['totalQuantity']>) => {
        return value ? formatNumber(value) : '-';
      },
    },
    {
      field: 'availableQuantity',
      headerName: '가용 재고',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '고객에게 판매 가능한 재고입니다.',
        tooltipMaxWidth: '182px',
      },
      cellClass: 'overflow-visible',
      cellStyle: { textAlign: 'right' },
      width: 96,
      valueFormatter: ({ value }: ValueFormatterParams<TypeStockDate, TypeStockDate['availableQuantity']>) => {
        return value ? formatNumber(value) : '-';
      },
    },
    {
      field: 'allocationQuantity',
      headerName: '작업중 재고',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '주문이 들어와 생산 중이거나, 이동 중인 재고입니다.',
        tooltipMaxWidth: '266px',
      },
      cellClass: 'overflow-visible',
      cellStyle: { textAlign: 'right' },
      width: 112,
      valueFormatter: ({ value }: ValueFormatterParams<TypeStockDate, TypeStockDate['allocationQuantity']>) => {
        return value ? formatNumber(value) : '-';
      },
    },
    {
      field: 'holdingQuantity',
      headerName: '보류 재고',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription:
          '가용 혹은 불용 재고 여부를 판단하기 위해 대기중인 재고,\n혹은 반출/폐기로 인해 센터에서 사라질 예정인 재고입니다.',
        tooltipMaxWidth: '299px',
      },
      width: 96,
      cellClass: 'overflow-visible',
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeStockDate, TypeStockDate['holdingQuantity']>) => {
        return value ? formatNumber(value) : '-';
      },
    },
    {
      field: 'defectiveQuantity',
      headerName: '불용 재고',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '판매 불가한 재고입니다.',
        tooltipMaxWidth: '137px',
      },
      width: 96,
      cellClass: 'overflow-visible',
      cellStyle: { textAlign: 'right' },
      valueFormatter: ({ value }: ValueFormatterParams<TypeStockDate, TypeStockDate['defectiveQuantity']>) => {
        return value ? formatNumber(value) : '-';
      },
    },
  ];

  return { columnDefs };
};

export default getStockDateColumnDefs;
